/**
 * Revision scope control for the Insights tab
 *
 * Renders the "Revision:" scope picker beside the "Report:" control in the table
 * header. Only visible when the journey has ≥ 2 revisions.
 *
 * Scope options:
 *   - "All revisions" → scope = 'all'
 *   - "Current revision" / "Revision N" → scope = revision_id
 *
 * Thin adapter over the shared {@link RevisionDropdown}: maps the scope value
 * ('all' | revision_id) onto the shared option model and back.
 */

import React from 'react'
import { HistoryOutlined } from '@ant-design/icons'
import type { RevisionMeta, RevisionScope } from './revision-insights'
import { revisionDisplayLabel } from './revision-insights'
import { RevisionDropdown, RevisionDropdownOption } from './revision-dropdown'

interface RevisionScopeSelectProps {
    revisions: RevisionMeta[]
    value: RevisionScope
    onChange: (scope: RevisionScope) => void
    /** Retained for call-site compatibility; the shared pill is a fixed (middle) height. */
    size?: 'small' | 'middle' | 'large'
    disabled?: boolean
}

export const RevisionScopeSelect: React.FC<RevisionScopeSelectProps> = ({ revisions, value, onChange }) => {
    const isScoped = value !== 'all'
    const selectedKey = value === 'all' ? 'all' : String(value)

    const scopedMeta = isScoped ? revisions.find((m) => String(m.id) === String(value)) : undefined
    const triggerText = scopedMeta ? revisionDisplayLabel(scopedMeta) : 'All revisions'

    // Newest-first to match the Workflow tab's RevisionSelector (both wrap the shared
    // RevisionDropdown; this keeps the two surfaces identical apart from the Insights-only
    // "All revisions" scope option, which has no Workflow equivalent).
    const options: RevisionDropdownOption[] = [
        { key: 'all', primary: 'All revisions' },
        ...[...revisions].reverse().map((meta) => ({
            key: String(meta.id),
            primary: revisionDisplayLabel(meta),
            sub: meta.dateRange,
            showActiveBadge: meta.isActive,
        })),
    ]

    return (
        <RevisionDropdown
            data-testid="revision-scope-select"
            rootClassName={`revision-scope-select${isScoped ? ' revision-scope-select--scoped' : ''}`}
            ariaLabel="Revision scope"
            icon={<HistoryOutlined style={{ fontSize: 13 }} />}
            prefix="Revision:"
            menuHeader="Revision history"
            triggerText={triggerText}
            options={options}
            selectedKey={selectedKey}
            onSelect={(key) => onChange(key === 'all' ? 'all' : Number(key))}
            minWidth={300}
        />
    )
}
